import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import Snackbar from '@mui/material/Snackbar'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { useCurrentUser } from '../../shared/hooks/useCurrentUser'
import { useAdminUsersQuery, useUpdateUserPermissionsMutation } from './hooks/useAdminUsersQuery'
import UserPermissionsDialog from './components/UserPermissionsDialog'
import {
  PERMISSIONS,
  ROLE_FILTERS,
  ROLE_FILTER_OPTIONS,
  USER_ROLES,
} from './administrationConstants'
import {
  canEditUser,
  filterUsers,
  getGrantedPermissions,
  getUserRole,
  summarizePermissions,
} from './administrationUtils'

const ROLE_CHIPS = {
  [USER_ROLES.SUPER_ADMINISTRATOR]: {
    label: 'Administrador geral',
    color: 'primary',
    variant: 'filled',
  },
  [USER_ROLES.ADMINISTRATOR]: { label: 'Administrador', color: 'primary', variant: 'outlined' },
  [USER_ROLES.SUPPORT]: { label: 'Suporte', color: 'default', variant: 'outlined' },
}

const AUTH_PROVIDER_LABELS = { firebase: 'Google' }

function resolveErrorMessage(error, fallback) {
  return error?.response?.data?.errorMessage || fallback
}

function UsersAdminPage() {
  const navigate = useNavigate()
  const {
    user: currentUser,
    isLoading: isLoadingCurrentUser,
    isAdministrator,
    isSuperAdministrator,
  } = useCurrentUser()
  const usersQuery = useAdminUsersQuery({ enabled: isAdministrator })
  const updateMutation = useUpdateUserPermissionsMutation()
  const [search, setSearch] = useState('')
  const [role, setRole] = useState(ROLE_FILTERS.ALL)
  const [editingUser, setEditingUser] = useState(null)
  const [snackbarMessage, setSnackbarMessage] = useState('')

  if (isLoadingCurrentUser) {
    return <LinearProgress />
  }

  if (!isAdministrator) {
    return (
      <Box>
        <Typography variant="h5" component="h2" gutterBottom>
          Usuários
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          Você não tem permissão para acessar a Administração.
        </Alert>
        <Button onClick={() => navigate('/workspace/customers')}>Voltar para Clientes</Button>
      </Box>
    )
  }

  const editor = { isSuperAdministrator }
  const users = filterUsers(usersQuery.data ?? [], { search, role })

  function handleSave(changes) {
    const target = editingUser
    updateMutation.mutate(
      { userId: target._id, permissions: changes },
      {
        onSuccess: () => {
          setEditingUser(null)
          setSnackbarMessage(`Permissões de ${target.name || target.login} atualizadas.`)
        },
        onError: (error) =>
          setSnackbarMessage(
            resolveErrorMessage(error, 'Erro ao salvar permissões. Tente novamente.'),
          ),
      },
    )
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        Administração › Usuários
      </Typography>
      <Typography variant="h5" component="h2" gutterBottom>
        Usuários
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Controle quem acessa cada tela e funcionalidade do Astrea Viewer.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          <TextField
            id="admin-users-search"
            label="Buscar por nome ou email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            fullWidth
          />
          <TextField
            id="admin-users-role"
            select
            label="Perfil"
            value={role}
            onChange={(event) => setRole(event.target.value)}
            sx={{ minWidth: 220 }}
          >
            {ROLE_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      {usersQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {resolveErrorMessage(usersQuery.error, 'Erro ao carregar usuários.')}
        </Alert>
      )}

      {usersQuery.isSuccess && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Mostrando: {users.length}
        </Typography>
      )}

      {usersQuery.isPending && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Usuário</TableCell>
              <TableCell>Perfil</TableCell>
              <TableCell>Permissões</TableCell>
              <TableCell>Login</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => {
              const roleChip = ROLE_CHIPS[getUserRole(user)]
              const grantedCount = getGrantedPermissions(user).length
              const isCurrentUser = user._id === currentUser?._id

              return (
                <TableRow key={user._id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {user.name || user.login}
                      {isCurrentUser && ' (você)'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.login}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={roleChip.label}
                      color={roleChip.color}
                      variant={roleChip.variant}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                      <LinearProgress
                        variant="determinate"
                        value={(grantedCount / PERMISSIONS.length) * 100}
                        aria-label={`${grantedCount} de ${PERMISSIONS.length} permissões`}
                        sx={{ width: 96 }}
                      />
                      <Typography variant="body2">
                        {grantedCount} de {PERMISSIONS.length}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {summarizePermissions(user)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {AUTH_PROVIDER_LABELS[user.authProvider] ?? 'Senha (legado)'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {canEditUser(editor, user) && (
                      <Button variant="outlined" size="small" onClick={() => setEditingUser(user)}>
                        Editar permissões
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
            {usersQuery.isSuccess && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhum usuário encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {editingUser && (
        <UserPermissionsDialog
          key={editingUser._id}
          user={editingUser}
          canManageAdministrators={isSuperAdministrator}
          isSaving={updateMutation.isPending}
          onCancel={() => setEditingUser(null)}
          onSave={handleSave}
        />
      )}

      <Snackbar
        open={snackbarMessage !== ''}
        autoHideDuration={5000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />
    </Box>
  )
}

export default UsersAdminPage
